import { Box } from '@mui/material';
import { ReactComponent as CloseIcon } from 'core/assets/svg/common/close_black.svg';
import BaseButton from 'core/components/button/BaseButton';
import { floatingAlertStyles } from 'core/components/floating-alert/FloatingAlertStyles';
import BaseTypography from 'core/components/typography/BaseTypography';
import { LocalStorageKeys } from 'core/constants/LocalStorageKeys';
import React, { useCallback, useEffect, useRef, useState } from 'react';

const MILLISECONDS_IN_MINUTE = 60000;
function FloatingAlert({
	title,
	subtitle = '',
	icon = null,
	actionText = '',
	onAction = () => {},
	dismissTime = 5000,
	onDismiss = () => {},
	resetTimeInMinutes = 12 * 60, // 12hours,
}) {
	const [isVisible, setIsVisible] = useState(false);
	const [isPaused, setIsPaused] = useState(false);
	const timerRef = useRef(null);

	const handleDismiss = useCallback(() => {
		setIsVisible(false);
		localStorage.setItem(LocalStorageKeys.FLOATING_ALERT, new Date().getTime().toString());
		onDismiss();
	}, [onDismiss]);

	const handleRefresh = () => {
		window.location.reload();
		localStorage.setItem(LocalStorageKeys.FLOATING_ALERT, new Date().getTime().toString());
	};

	useEffect(() => {
		const resetTimeInMilliseconds = resetTimeInMinutes * MILLISECONDS_IN_MINUTE;

		const checkAndShowAlert = () => {
			const lastDismissed = localStorage.getItem(LocalStorageKeys.FLOATING_ALERT);
			const currentTime = new Date().getTime();
			const timeElapsed = currentTime - parseInt(lastDismissed, 10);

			if (!lastDismissed || timeElapsed >= resetTimeInMilliseconds) {
				setIsVisible(true);
			}
		};

		// Check immediately when the component mounts
		checkAndShowAlert();

		// Set up an interval to check periodically
		const intervalId = setInterval(checkAndShowAlert, resetTimeInMilliseconds / 2);

		// Clean up the interval when the component unmounts
		return () => clearInterval(intervalId);
	}, [resetTimeInMinutes]);

	useEffect(() => {
		if (isVisible && !isPaused) {
			timerRef.current = setTimeout(() => {
				handleDismiss();
			}, dismissTime);
		}

		return () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
			}
		};
	}, [isVisible, isPaused, dismissTime, handleDismiss]);

	const handleMouseEnter = () => {
		setIsPaused(true);
		if (timerRef.current) {
			clearTimeout(timerRef.current);
		}
	};

	const handleMouseLeave = () => {
		setIsPaused(false);
	};

	if (!isVisible) return null;

	return (
		<Box
			id="floating-alert"
			sx={floatingAlertStyles.floatingAlert}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
		>
			<Box id="refresh-icon" sx={{ cursor: 'pointer' }} onClick={handleRefresh}>
				{icon}
			</Box>
			<BaseTypography
				id="alert-text"
				variant="t2_medium"
				styles={{ color: 'colors.light3', textAlign: 'left' }}
			>
				{title}
				<br />
				{subtitle}
			</BaseTypography>
			{actionText && (
				<BaseButton id="alert-action" sx={floatingAlertStyles.updateButton} onClick={onAction}>
					{actionText}
				</BaseButton>
			)}
			<Box id="alert-dismiss" sx={floatingAlertStyles.alertDismiss} onClick={handleDismiss}>
				<CloseIcon />
			</Box>
		</Box>
	);
}

export default FloatingAlert;
